import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as s from '@/components/legal-styles'

export const Route = createFileRoute('/legal/sla')({
  component: SlaPage,
})

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14, lineHeight: 1.7,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #EDE9E4',
  fontWeight: 700, color: '#1a1216', fontSize: 13,
}
const tdStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #EDE9E4', color: '#4a4745', fontSize: 14,
}

function SlaPage() {
  return (
    <div>
      <Helmet>
        <title>Service Level Agreement | One-Email</title>
        <meta name="description" content="One-Email Service Level Agreement (SLA). Uptime commitments, service credits, exclusions, and email delivery guarantees for paid plans." />
        <link rel="canonical" href="https://one-email.com/legal/sla" />
      </Helmet>

      <h1 style={s.heading}>Service Level Agreement</h1>
      <p style={s.updated}>Last updated: March 24, 2026</p>

      <div style={s.highlight}>
        <strong>Summary:</strong> We commit to specific uptime guarantees for paid plans. If we miss them, you get service credits. Free tier is best-effort with no SLA.
      </div>

      {/* 1. Scope */}
      <h2 id="scope" style={s.h2}>1. Scope</h2>
      <p style={s.p}>
        This SLA applies to the One-Email API (<code>api.one-email.com</code>) and Dashboard (<code>dashboard.one-email.com</code>) for customers on paid plans. It does not apply to the Free tier, which is provided on a best-effort basis.
      </p>

      {/* 2. Uptime Commitments */}
      <h2 id="uptime" style={s.h2}>2. Uptime Commitments</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Tier</th>
              <th style={thStyle}>Monthly Uptime Commitment</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Free</td><td style={tdStyle}>No SLA (best-effort)</td></tr>
            <tr><td style={tdStyle}>Starter</td><td style={tdStyle}>99.9%</td></tr>
            <tr><td style={tdStyle}>Pro</td><td style={tdStyle}>99.95%</td></tr>
            <tr><td style={tdStyle}>Growth</td><td style={tdStyle}>99.95%</td></tr>
            <tr><td style={tdStyle}>Scale</td><td style={tdStyle}>99.99%</td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        <strong>&ldquo;Uptime&rdquo;</strong> means the percentage of time during a calendar month that the One-Email API is available and responding to valid requests. Uptime is calculated as:
      </p>
      <div style={{
        background: '#f5f2ef', padding: '16px 20px', borderRadius: 8,
        fontFamily: 'var(--oe-font-mono)', fontSize: 14, marginBottom: 24,
        overflowX: 'auto',
      }}>
        Uptime % = ((Total Minutes in Month - Downtime Minutes) / Total Minutes in Month) &times; 100
      </div>
      <p style={s.p}>
        <strong>&ldquo;Downtime&rdquo;</strong> means any period of 5 or more consecutive minutes during which the API returns errors (HTTP 5xx) or does not respond to valid requests for more than 50% of requests. Periods shorter than 5 consecutive minutes are not counted as Downtime.
      </p>

      {/* 3. Service Credits */}
      <h2 id="credits" style={s.h2}>3. Service Credits</h2>
      <p style={s.p}>
        If we fail to meet the uptime commitment for your tier in any calendar month, you are entitled to a service credit applied to your next monthly invoice.
      </p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Uptime Achieved</th>
              <th style={thStyle}>Credit (% of monthly fee)</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Below commitment but above commitment - 0.5%</td><td style={tdStyle}>10%</td></tr>
            <tr><td style={tdStyle}>Below commitment - 0.5% but above commitment - 1.0%</td><td style={tdStyle}>25%</td></tr>
            <tr><td style={tdStyle}>Below commitment - 1.0%</td><td style={tdStyle}>50%</td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        <strong>Example:</strong> If you are on the Pro tier (99.95% commitment) and actual uptime is 99.3%, you are below 99.95% by more than 0.5% but less than 1.0%, so you receive a 25% credit.
      </p>
      <p style={s.p}>
        Maximum credit in any month: <strong>50% of that month&apos;s subscription fee</strong> (excluding overage charges).
      </p>

      {/* 4. How to Claim */}
      <h2 id="claim" style={s.h2}>4. How to Claim</h2>
      <p style={s.p}>To receive a service credit:</p>
      <ol style={{ ...s.ul, listStyleType: 'decimal' }}>
        <li style={s.li}>Submit a claim to <strong>support@one-email.com</strong> within <strong>30 days</strong> of the end of the month in which the Downtime occurred.</li>
        <li style={s.li}>Include: your account ID, the dates and times of observed Downtime, and a brief description.</li>
        <li style={s.li}>We will verify the claim against our monitoring data and respond within 10 business days.</li>
        <li style={s.li}>If verified, the credit will be applied to your next invoice. Credits are not redeemable for cash and do not carry over beyond 12 months.</li>
      </ol>

      {/* 5. Exclusions */}
      <h2 id="exclusions" style={s.h2}>5. Exclusions</h2>
      <p style={s.p}>This SLA does not apply to Downtime caused by:</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Scheduled maintenance</strong> &mdash; announced at least 24 hours in advance via email or dashboard notification</li>
        <li style={s.li}><strong>Force majeure events</strong> &mdash; natural disasters, war, government action, pandemic, widespread internet outages</li>
        <li style={s.li}><strong>Your actions</strong> &mdash; misconfigured DNS, compromised API keys, sending in violation of the AUP, exceeding rate limits</li>
        <li style={s.li}><strong>DDoS attacks</strong> or other security events outside our reasonable control</li>
        <li style={s.li}><strong>Third-party failures</strong> &mdash; upstream network or ISP outages beyond our infrastructure</li>
        <li style={s.li}><strong>Beta or preview features</strong> &mdash; any feature explicitly labeled as beta, preview, or experimental</li>
      </ul>

      {/* 6. What This SLA Does NOT Cover */}
      <h2 id="not-covered" style={s.h2}>6. What This SLA Does NOT Cover</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Email delivery success rates.</strong> We deliver your email to the recipient&apos;s mail server, but we cannot guarantee inbox placement. Whether an email reaches the inbox depends on the recipient&apos;s server policies, your domain reputation, and your sending practices.</li>
        <li style={s.li}><strong>Webhook delivery latency.</strong> Webhooks are delivered on a best-effort basis.</li>
        <li style={s.li}><strong>Third-party integrations.</strong> Issues with Polar billing, Vercel auto-configure, or other integrated services.</li>
      </ul>

      {/* 7. Email Delivery Commitment */}
      <h2 id="delivery" style={s.h2}>7. Email Delivery Commitment</h2>
      <p style={s.p}>
        While we cannot guarantee inbox placement (no email provider can), we commit to:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Attempting delivery</strong> of every queued email. We do not silently drop emails.</li>
        <li style={s.li}><strong>Retry logic</strong> for temporary delivery failures (SMTP 4xx responses), with exponential backoff for up to 72 hours.</li>
        <li style={s.li}><strong>Transparent status</strong> &mdash; every email&apos;s delivery status is available via the API and dashboard in real time.</li>
        <li style={s.li}><strong>No silent fallback</strong> &mdash; we do not route your email through any third-party service without your knowledge.</li>
      </ul>

      {/* 8. Sole Remedy */}
      <h2 id="remedy" style={s.h2}>8. Sole Remedy</h2>
      <p style={s.p}>
        Service credits are your sole and exclusive remedy for any failure to meet this SLA. This SLA does not modify or supersede the limitation of liability in the <a href="/legal/terms">Terms of Service</a>.
      </p>

      {/* 9. Changes */}
      <h2 id="changes" style={s.h2}>9. Changes</h2>
      <p style={s.p}>
        We may update this SLA with at least <strong>30 days notice</strong>. Changes will not reduce uptime commitments for your current tier during your billing period.
      </p>
    </div>
  )
}
